import { useMemo, useState } from 'react'
import {
  ArrowDown, ArrowLeft, ArrowUp, ClipboardCheck, Download, Eye, LayoutGrid, ListOrdered, Pencil,
  Lock, Monitor, Puzzle, RotateCcw, Rows3, Save, ShieldCheck, Sparkles, SlidersHorizontal, Trash2, TriangleAlert, Upload,
} from 'lucide-react'
import { APP_VERSION } from '../constants'
import { withAddons } from '../lib/addons'
import {
  DEFAULT_CONFIG, FEATURE_LABELS, ROW_KIND_LABELS, SECTION_LABELS,
  exportConfig, importConfig, moveNav, moveRow, posterWidthRem,
  type AdminConfig, type CodecPolicy, type FeatureFlags,
} from '../lib/config'
import { currentTvModel } from '../lib/diagnostics'
import { getMeasuredSpeed } from '../lib/storage'
import type { AddonConfig, Settings } from '../types'
import { AdminChoice, AdminStepper, AdminSwatches, AdminToggle } from './AdminControls'
import { GridKeyboard } from './GridKeyboard'

type PanelId = 'layout' | 'rows' | 'hero' | 'features' | 'appearance' | 'playback' | 'addons' | 'access' | 'backup' | 'about'

const PANELS: Array<{ id: PanelId; label: string; icon: typeof LayoutGrid }> = [
  { id: 'layout', label: 'Destinations', icon: LayoutGrid },
  { id: 'rows', label: 'Home rows', icon: Rows3 },
  { id: 'hero', label: 'Spotlight', icon: Sparkles },
  { id: 'features', label: 'Features', icon: ListOrdered },
  { id: 'appearance', label: 'Appearance', icon: Eye },
  { id: 'playback', label: 'Playback', icon: SlidersHorizontal },
  { id: 'addons', label: 'Add-ons', icon: Puzzle },
  { id: 'access', label: 'Access', icon: Lock },
  { id: 'backup', label: 'Backup', icon: Save },
  { id: 'about', label: 'This TV', icon: Monitor },
]

const ACCENTS = [
  { value: '#d8ff3e', label: 'Vela lime' },
  { value: '#5ac8fa', label: 'Ice' },
  { value: '#ff6f61', label: 'Coral' },
  { value: '#a78bfa', label: 'Violet' },
  { value: '#ffc85c', label: 'Amber' },
  { value: '#55d2a1', label: 'Mint' },
  { value: '#f5f6f8', label: 'Paper' },
]

interface Props {
  config: AdminConfig
  settings: Settings
  onChange: (config: AdminConfig) => void
  onSaveSettings: (settings: Settings) => void
  onClose: () => void
}

export function AdminOverlay({ config, settings, onChange, onSaveSettings, onClose }: Props) {
  const [panel, setPanel] = useState<PanelId>('layout')
  const [unlocked, setUnlocked] = useState(!config.pin)
  const [pinEntry, setPinEntry] = useState('')
  const [pinError, setPinError] = useState('')
  const [newPin, setNewPin] = useState('')
  const [transfer, setTransfer] = useState('')
  const [transferNote, setTransferNote] = useState('')
  const [confirmReset, setConfirmReset] = useState(false)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [draftTitle, setDraftTitle] = useState('')

  const patch = (part: Partial<AdminConfig>) => onChange({ ...config, ...part })
  const setFeature = (key: keyof FeatureFlags, value: boolean) =>
    patch({ features: { ...config.features, [key]: value } })

  const measured = getMeasuredSpeed()
  const cardWidth = posterWidthRem(config.appearance.postersPerRow, config.appearance.safeArea)

  const disabledCount = useMemo(() => ({
    sections: config.nav.filter((item) => !item.enabled).length,
    rows: config.rows.filter((row) => !row.enabled).length,
    features: Object.values(config.features).filter((value) => !value).length,
  }), [config])

  /* ---------------- PIN gate ---------------- */

  if (!unlocked) {
    return (
      <div className="admin-overlay" role="dialog" aria-label="Admin controls locked">
        <div className="admin-lock">
          <span className="setup-mark"><ShieldCheck size="2.25rem" /></span>
          <h1>Admin controls</h1>
          <p>Enter the PIN for this TV to change what Vela shows.</p>
          <div className="admin-pin-display" aria-live="polite">
            {pinEntry ? <b>{'\u2022'.repeat(pinEntry.length)}</b> : <em>Enter PIN</em>}
          </div>
          {pinError ? <p className="admin-error">{pinError}</p> : null}
          <GridKeyboard
            mode="digits"
            navKey="admin-pin"
            onKey={(character) => { setPinError(''); setPinEntry((value) => (value + character).slice(0, 8)) }}
            onBackspace={() => setPinEntry((value) => value.slice(0, -1))}
            onClear={() => setPinEntry('')}
          />
          <div className="admin-lock-actions" data-nav-group="row" data-nav-key="admin-lock">
            <button
              className="primary-button"
              data-focusable
              onClick={() => {
                if (pinEntry === config.pin) setUnlocked(true)
                else { setPinError('That PIN does not match.'); setPinEntry('') }
              }}
            >Unlock</button>
            <button className="secondary-button" data-focusable onClick={onClose}>
              <ArrowLeft size="1.5rem" /> Back
            </button>
          </div>
        </div>
      </div>
    )
  }

  /* ---------------- panels ---------------- */

  function renderPanel() {
    switch (panel) {
      case 'layout':
        return (
          <>
            <PanelHead
              title="Destinations"
              detail="Which places exist in the top navigation, and in what order. Home cannot be switched off."
            />
            <div className="admin-list" data-nav-group="col" data-nav-key="admin-nav-list">
              {config.nav.map((item, index) => (
                <div className={`admin-item${item.enabled ? '' : ' is-off'}`} key={item.id}>
                  <span className="admin-item-copy">
                    <strong>{SECTION_LABELS[item.id]}</strong>
                    <small>{item.enabled ? `Position ${index + 1}` : 'Hidden'}</small>
                  </span>
                  <span className="admin-item-actions" data-nav-group="row" data-nav-key={`nav:${item.id}`}>
                    <button className="stepper-button" data-focusable disabled={index === 0} aria-label={`Move ${SECTION_LABELS[item.id]} earlier`} onClick={() => patch({ nav: moveNav(config.nav, item.id, -1) })}><ArrowUp size="1.5rem" /></button>
                    <button className="stepper-button" data-focusable disabled={index === config.nav.length - 1} aria-label={`Move ${SECTION_LABELS[item.id]} later`} onClick={() => patch({ nav: moveNav(config.nav, item.id, 1) })}><ArrowDown size="1.5rem" /></button>
                    <button
                      className={`toggle${item.enabled ? ' on' : ''}`}
                      data-focusable
                      disabled={item.id === 'home'}
                      aria-label={`Show ${SECTION_LABELS[item.id]}`}
                      onClick={() => patch({ nav: config.nav.map((entry) => (entry.id === item.id ? { ...entry, enabled: !entry.enabled } : entry)) })}
                    ><i /></button>
                  </span>
                </div>
              ))}
            </div>
          </>
        )

      case 'rows':
        return (
          <>
            <PanelHead
              title="Home rows"
              detail="Every row on the home screen, in the order it appears. Rows with nothing to show hide themselves."
            />
            {renaming ? (
              <div className="admin-rename">
                <p className="admin-note">Renaming <strong>{config.rows.find((row) => row.id === renaming)?.title}</strong></p>
                <div className="admin-pin-display admin-title-display">{draftTitle || 'Row heading'}</div>
                <GridKeyboard
                  navKey="admin-rename"
                  onKey={(character) => setDraftTitle((value) => (value + character).slice(0, 40))}
                  onBackspace={() => setDraftTitle((value) => value.slice(0, -1))}
                  onClear={() => setDraftTitle('')}
                />
                <div className="admin-actions" data-nav-group="row" data-nav-key="admin-rename-actions">
                  <button
                    className="primary-button"
                    data-focusable
                    disabled={!draftTitle.trim()}
                    onClick={() => {
                      patch({ rows: config.rows.map((row) => (row.id === renaming ? { ...row, title: draftTitle.trim() } : row)) })
                      setRenaming(null)
                    }}
                  >Save heading</button>
                  <button className="secondary-button" data-focusable onClick={() => setRenaming(null)}>Cancel</button>
                </div>
              </div>
            ) : (
            <div className="admin-list" data-nav-group="col" data-nav-key="admin-rows-list">
              {config.rows.map((row, index) => (
                <div className={`admin-item${row.enabled ? '' : ' is-off'}`} key={row.id}>
                  <span className="admin-item-copy">
                    <strong>{row.title}</strong>
                    <small>{ROW_KIND_LABELS[row.kind]}{row.type ? ` · ${row.type === 'movie' ? 'Films' : 'Series'}` : ''} · up to {row.limit}</small>
                  </span>
                  <span className="admin-item-actions" data-nav-group="row" data-nav-key={`row:${row.id}`}>
                    <button className="stepper-button" data-focusable disabled={row.limit <= 4} aria-label={`Fewer items in ${row.title}`} onClick={() => patch({ rows: config.rows.map((entry) => (entry.id === row.id ? { ...entry, limit: Math.max(4, entry.limit - 2) } : entry)) })}>&minus;</button>
                    <button className="stepper-button" data-focusable disabled={row.limit >= 40} aria-label={`More items in ${row.title}`} onClick={() => patch({ rows: config.rows.map((entry) => (entry.id === row.id ? { ...entry, limit: Math.min(40, entry.limit + 2) } : entry)) })}>+</button>
                    <button className="stepper-button" data-focusable disabled={index === 0} aria-label={`Move ${row.title} up`} onClick={() => patch({ rows: moveRow(config.rows, row.id, -1) })}><ArrowUp size="1.5rem" /></button>
                    <button className="stepper-button" data-focusable disabled={index === config.rows.length - 1} aria-label={`Move ${row.title} down`} onClick={() => patch({ rows: moveRow(config.rows, row.id, 1) })}><ArrowDown size="1.5rem" /></button>
                    <button className="stepper-button" data-focusable aria-label={`Rename ${row.title}`} onClick={() => { setRenaming(row.id); setDraftTitle(row.title) }}><Pencil size="1.25rem" /></button>
                    <button
                      className={`toggle${row.enabled ? ' on' : ''}`}
                      data-focusable
                      aria-label={`Show ${row.title}`}
                      onClick={() => patch({ rows: config.rows.map((entry) => (entry.id === row.id ? { ...entry, enabled: !entry.enabled } : entry)) })}
                    ><i /></button>
                  </span>
                </div>
              ))}
            </div>
            )}
          </>
        )

      case 'hero':
        return (
          <>
            <PanelHead title="Spotlight" detail="The full-width feature at the top of the home screen." />
            <AdminToggle label="Show the spotlight" detail="Turn it off for a rows-only home screen." value={config.hero.enabled} onChange={(enabled) => patch({ hero: { ...config.hero, enabled } })} />
            <AdminToggle label="Rotate through titles" detail="Move to the next title on its own." value={config.hero.rotate} onChange={(rotate) => patch({ hero: { ...config.hero, rotate } })} />
            <AdminStepper label="Seconds on each title" value={config.hero.intervalSeconds} min={3} max={60} step={1} format={(value) => `${value}s`} onChange={(intervalSeconds) => patch({ hero: { ...config.hero, intervalSeconds } })} />
            <AdminStepper label="Titles in rotation" value={config.hero.count} min={1} max={12} onChange={(count) => patch({ hero: { ...config.hero, count } })} />
            <AdminChoice
              label="Pull from"
              value={config.hero.source}
              options={[{ value: 'mixed', label: 'Films and series' }, { value: 'movie', label: 'Films only' }, { value: 'series', label: 'Series only' }]}
              onChange={(source) => patch({ hero: { ...config.hero, source } })}
            />
            <AdminToggle label="Show the synopsis" value={config.hero.showDescription} onChange={(showDescription) => patch({ hero: { ...config.hero, showDescription } })} />
          </>
        )

      case 'features':
        return (
          <>
            <PanelHead title="Features" detail="Switch parts of the app off entirely. Anything off here disappears from every screen." />
            {(Object.keys(FEATURE_LABELS) as Array<keyof FeatureFlags>).map((key) => (
              <AdminToggle
                key={key}
                label={FEATURE_LABELS[key].title}
                detail={FEATURE_LABELS[key].detail}
                value={config.features[key]}
                onChange={(value) => setFeature(key, value)}
              />
            ))}
          </>
        )

      case 'appearance':
        return (
          <>
            <PanelHead title="Appearance" detail="How the interface looks and how strongly it reacts to focus." />
            <AdminSwatches label="Accent colour" detail="Used for focus, progress and primary actions. Label contrast follows automatically." value={config.appearance.accent} options={ACCENTS} onChange={(accent) => patch({ appearance: { ...config.appearance, accent } })} />
            <AdminStepper
              label="Posters across the screen"
              detail={`Cards are ${cardWidth.toFixed(1)}rem wide at this setting.`}
              value={config.appearance.postersPerRow}
              min={3}
              max={10}
              onChange={(postersPerRow) => patch({ appearance: { ...config.appearance, postersPerRow } })}
            />
            <AdminChoice
              label="Titles under posters"
              detail="Showing them only on focus keeps rows clean and lets posters be larger."
              value={config.appearance.captionMode}
              options={[{ value: 'focus', label: 'On focus' }, { value: 'always', label: 'Always' }]}
              onChange={(captionMode) => patch({ appearance: { ...config.appearance, captionMode } })}
            />
            <AdminStepper label="Focus growth" detail="How much the focused card grows." value={config.appearance.focusScale} min={1} max={1.3} step={0.02} format={(value) => `${Math.round((value - 1) * 100)}%`} onChange={(focusScale) => patch({ appearance: { ...config.appearance, focusScale } })} />
            <AdminToggle label="Dim unfocused cards" detail="The strongest cue at a distance." value={config.appearance.dimSiblings} onChange={(dimSiblings) => patch({ appearance: { ...config.appearance, dimSiblings } })} />
            <AdminStepper label="Screen margin" value={config.appearance.safeArea} min={1} max={10} step={0.5} format={(value) => `${Math.round(value * 16)}px`} onChange={(safeArea) => patch({ appearance: { ...config.appearance, safeArea } })} />
            <AdminToggle label="Animations" detail="Turn off on a slower panel to keep focus moves instant." value={config.appearance.animations} onChange={(animations) => patch({ appearance: { ...config.appearance, animations } })} />
            <AdminToggle label="Clock" value={config.appearance.showClock} onChange={(showClock) => patch({ appearance: { ...config.appearance, showClock } })} />
            <AdminToggle label="Server status" value={config.appearance.showServerStatus} onChange={(showServerStatus) => patch({ appearance: { ...config.appearance, showServerStatus } })} />
          </>
        )

      case 'playback':
        return (
          <>
            <PanelHead title="Playback" detail="How the player behaves and which files Vela is willing to recommend." />
            <AdminStepper label="Skip step" value={config.playback.seekStep} min={5} max={60} step={5} format={(value) => `${value}s`} onChange={(seekStep) => patch({ playback: { ...config.playback, seekStep } })} />
            <AdminStepper label="Long skip step" value={config.playback.bigSeekStep} min={15} max={600} step={15} format={(value) => `${value}s`} onChange={(bigSeekStep) => patch({ playback: { ...config.playback, bigSeekStep } })} />
            <AdminStepper label="Hide controls after" value={config.playback.chromeHideSeconds} min={1} max={30} step={0.5} format={(value) => `${value}s`} onChange={(chromeHideSeconds) => patch({ playback: { ...config.playback, chromeHideSeconds } })} />
            <AdminChoice<CodecPolicy>
              label="Codec policy"
              detail="Strict hides anything this panel is unlikely to decode. Balanced hides only what it has reported or proven it cannot. Any hides nothing. All three still rank playable sources first."
              value={config.playback.codecPolicy}
              options={[{ value: 'strict', label: 'Strict' }, { value: 'balanced', label: 'Balanced' }, { value: 'any', label: 'Any' }]}
              onChange={(codecPolicy) => patch({ playback: { ...config.playback, codecPolicy } })}
            />
            <AdminStepper
              label="Largest file offered"
              detail="Sources above this are hidden. Zero means no limit."
              value={config.playback.maxFileSizeGb}
              min={0}
              max={200}
              step={5}
              format={(value) => (value ? `${value} GB` : 'No limit')}
              onChange={(maxFileSizeGb) => patch({ playback: { ...config.playback, maxFileSizeGb } })}
            />
            <p className="admin-note">
              {measured
                ? `This connection has been measured at ${(measured / (1024 * 1024)).toFixed(1)} MB/s. Sources are ranked against it.`
                : 'Vela will measure the connection during playback and rank sources against it.'}
            </p>
          </>
        )

      case 'addons': {
        const addons = settings.addons || []
        const setAddons = (next: AddonConfig[]) => onSaveSettings(withAddons(settings, next))
        return (
          <>
            <PanelHead title="Add-ons" detail="Where sources and subtitles come from, in the order they are asked." />
            <div className="admin-list" data-nav-group="col" data-nav-key="admin-addons">
              {addons.length ? addons.map((addon, index) => (
                <div className={`admin-item${addon.enabled ? '' : ' is-off'}`} key={addon.id}>
                  <span className="admin-item-copy">
                    <strong>{addon.name}</strong>
                    <small>{(addon.resources || []).join(' · ') || 'unknown'} · {addon.url.replace(/^https?:\/\//, '')}</small>
                  </span>
                  <span className="admin-item-actions" data-nav-group="row" data-nav-key={`addon:${addon.id}`}>
                    <button className="stepper-button" data-focusable disabled={index === 0} aria-label={`Ask ${addon.name} earlier`} onClick={() => {
                      const next = addons.slice()
                      next.splice(index - 1, 0, next.splice(index, 1)[0])
                      setAddons(next.map((entry, position) => ({ ...entry, priority: position })))
                    }}><ArrowUp size="1.5rem" /></button>
                    <button className="stepper-button" data-focusable disabled={index === addons.length - 1} aria-label={`Ask ${addon.name} later`} onClick={() => {
                      const next = addons.slice()
                      next.splice(index + 1, 0, next.splice(index, 1)[0])
                      setAddons(next.map((entry, position) => ({ ...entry, priority: position })))
                    }}><ArrowDown size="1.5rem" /></button>
                    <button className="stepper-button" data-focusable aria-label={`Remove ${addon.name}`} onClick={() => setAddons(addons.filter((entry) => entry.id !== addon.id))}><Trash2 size="1.25rem" /></button>
                    <button
                      className={`toggle${addon.enabled ? ' on' : ''}`}
                      data-focusable
                      aria-label={`Use ${addon.name}`}
                      onClick={() => setAddons(addons.map((entry) => (entry.id === addon.id ? { ...entry, enabled: !entry.enabled } : entry)))}
                    ><i /></button>
                  </span>
                </div>
              )) : <p className="admin-note">No add-ons configured. Vela cannot find sources without at least one.</p>}
            </div>
            <p className="admin-note">Add a new add-on from Settings, where its address can be typed and tested.</p>
          </>
        )
      }

      case 'access':
        return (
          <>
            <PanelHead title="Access" detail="A PIN keeps these controls away from anyone holding the remote." />
            <p className="admin-note">
              {config.pin ? `Admin controls are protected by a ${config.pin.length}-digit PIN.` : 'Admin controls are currently unprotected.'}
            </p>
            <div className="admin-pin-display" aria-live="polite">
              {newPin ? <b>{'\u2022'.repeat(newPin.length)}</b> : <em>New PIN</em>}
            </div>
            <GridKeyboard
              mode="digits"
              navKey="admin-newpin"
              autofocus={false}
              onKey={(character) => setNewPin((value) => (value + character).slice(0, 8))}
              onBackspace={() => setNewPin((value) => value.slice(0, -1))}
              onClear={() => setNewPin('')}
            />
            <div className="admin-actions" data-nav-group="row" data-nav-key="admin-pin-actions">
              <button className="primary-button" data-focusable disabled={newPin.length < 4} onClick={() => { patch({ pin: newPin }); setNewPin('') }}>
                <Lock size="1.5rem" /> Set PIN
              </button>
              <button className="secondary-button" data-focusable disabled={!config.pin} onClick={() => { patch({ pin: '' }); setNewPin('') }}>
                Remove PIN
              </button>
            </div>
            <p className="admin-note">A PIN must be at least four digits. Removing it leaves these controls open.</p>
          </>
        )

      case 'backup':
        return (
          <>
            <PanelHead title="Backup" detail="Move this exact configuration to another TV, or start again." />
            <div className="admin-actions" data-nav-group="row" data-nav-key="admin-backup">
              <button className="secondary-button" data-focusable onClick={() => { setTransfer(exportConfig(config)); setTransferNote('Configuration ready below. Copy it to another TV.') }}>
                <Download size="1.5rem" /> Export
              </button>
              <button className="secondary-button" data-focusable onClick={async () => {
                try {
                  const text = await navigator.clipboard.readText()
                  const result = importConfig(text, config)
                  if (result.config) { onChange(result.config); setTransferNote('Configuration applied from the clipboard.') }
                  else setTransferNote(result.error || 'Nothing usable on the clipboard.')
                } catch {
                  setTransferNote('This TV did not allow reading the clipboard.')
                }
              }}>
                <Upload size="1.5rem" /> Import from clipboard
              </button>
              <button className="secondary-button" data-focusable onClick={async () => {
                try {
                  await navigator.clipboard.writeText(exportConfig(config))
                  setTransferNote('Configuration copied to the clipboard.')
                } catch {
                  setTransferNote('This TV did not allow writing to the clipboard.')
                }
              }}>
                <ClipboardCheck size="1.5rem" /> Copy
              </button>
            </div>
            {transferNote ? <p className="admin-note">{transferNote}</p> : null}
            {transfer ? <pre className="admin-transfer" data-focusable tabIndex={0}>{transfer}</pre> : null}

            <div className="admin-danger">
              <div className="admin-row-copy">
                <h3><TriangleAlert size="1.5rem" /> Reset everything</h3>
                <p>Restores every destination, row, feature and appearance setting to the defaults Vela ships with. Viewer settings and watch history are untouched.</p>
              </div>
              <div className="admin-actions" data-nav-group="row" data-nav-key="admin-reset">
                {confirmReset ? (
                  <>
                    <button className="primary-button danger" data-focusable onClick={() => { onChange({ ...DEFAULT_CONFIG }); setConfirmReset(false); setTransferNote('Configuration reset to defaults.') }}>
                      <RotateCcw size="1.5rem" /> Yes, reset
                    </button>
                    <button className="secondary-button" data-focusable onClick={() => setConfirmReset(false)}>Keep my settings</button>
                  </>
                ) : (
                  <button className="secondary-button" data-focusable onClick={() => setConfirmReset(true)}>
                    <RotateCcw size="1.5rem" /> Reset to defaults
                  </button>
                )}
              </div>
            </div>
          </>
        )

      case 'about':
        return (
          <>
            <PanelHead title="This TV" detail="What Vela has worked out about the device it is running on." />
            <dl className="admin-facts">
              <div><dt>Vela</dt><dd>{APP_VERSION}</dd></div>
              <div><dt>Model</dt><dd>{currentTvModel() || 'Not reported'}</dd></div>
              <div><dt>Streaming server</dt><dd>{settings.serverUrl.replace(/^https?:\/\//, '')}</dd></div>
              <div><dt>Add-ons in use</dt><dd>{(settings.addons || []).filter((addon) => addon.enabled).length} of {(settings.addons || []).length}</dd></div>
              <div><dt>Measured speed</dt><dd>{measured ? `${(measured / (1024 * 1024)).toFixed(1)} MB/s` : 'Not measured yet'}</dd></div>
              <div><dt>Screen</dt><dd>{window.innerWidth}&times;{window.innerHeight}</dd></div>
              <div><dt>Hidden destinations</dt><dd>{disabledCount.sections}</dd></div>
              <div><dt>Hidden rows</dt><dd>{disabledCount.rows}</dd></div>
              <div><dt>Features off</dt><dd>{disabledCount.features}</dd></div>
            </dl>
          </>
        )

      default:
        return null
    }
  }

  return (
    <div className="admin-overlay" role="dialog" aria-label="Admin controls">
      <header className="admin-head">
        <button className="round-button" data-focusable data-autofocus onClick={onClose} aria-label="Close admin controls">
          <ArrowLeft size="2rem" />
        </button>
        <div>
          <small>ADMIN CONTROLS</small>
          <strong>What this TV shows</strong>
        </div>
        <span className="admin-summary">
          {disabledCount.sections + disabledCount.rows + disabledCount.features === 0
            ? 'Everything switched on'
            : `${disabledCount.sections} destinations, ${disabledCount.rows} rows and ${disabledCount.features} features off`}
        </span>
      </header>

      <div className="admin-body">
        <nav className="admin-nav" data-nav-group="col" data-nav-key="admin-panels">
          {PANELS.map((entry) => {
            const Icon = entry.icon
            return (
              <button
                key={entry.id}
                className={`admin-tab${panel === entry.id ? ' active' : ''}`}
                data-focusable
                aria-pressed={panel === entry.id}
                onClick={() => setPanel(entry.id)}
              >
                <Icon size="1.75rem" /><span>{entry.label}</span>
              </button>
            )
          })}
        </nav>
        <div className="admin-panel" key={panel}>
          {renderPanel()}
        </div>
      </div>
    </div>
  )
}

function PanelHead({ title, detail }: { title: string; detail: string }) {
  return (
    <header className="admin-panel-head">
      <h2>{title}</h2>
      <p>{detail}</p>
    </header>
  )
}
